import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, filter, takeUntil, withLatestFrom } from 'rxjs';
import { selectIsPtrs, selectSubAccountId } from '../../../account-info-header/reducers';
import { ModifiedFirstPartiesActions } from '../../../modified-first-parties/actions';
import { AdobeConstants } from '../../../shared/adobe-tagging/AdobeConstants';
import { AdobeHelpers } from '../../../shared/adobe-tagging/adobeHelper';
import { yesNoModel } from '../../../shared/controlModels/radioModels';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { Constants } from '../../../shared/utilities/Constants';
import { PtrsActions, WireActions } from '../../actions';
import { RadioControl } from '../../models/form/FormControlRadio';
import { Drafts } from '../../models/wire/WireTemplatesResponse';
import { selectIsFromEdit, selectTemplateId, selectUseTemplateVal, selectWireInputV3CallFailed, selectWireInputV3CallFinished } from '../../reducers';
import { createControl } from '../../utilities/formUtilities';
import { MailingDetails } from '../../models/wire/Intermediary';
import { CardControl } from '../../../shared/models/CardControl';
import { selectEnableUseSloaContainer } from '../../../feature-flags/reducers';
import { StandingAuthorizationCard } from '../../../shared/components/standing-authorization-card-container/standing-authorization-card-container.component';

@Component({
  standalone: false,
  selector: 'mm-wire-templates',
  templateUrl: './wire-templates.component.html',
  styleUrls: ['./wire-templates.component.scss'],
})
export class WireTemplatesComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() parentForm!: UntypedFormGroup;
  @Input() templates$: Observable<Drafts[]>;
  destroy$ = new Subject<boolean>();
  showTemplateOptions: boolean;
  useTemplatesModel: RadioControl = yesNoModel(
    'Use a saved template?',
    'useTemplates'
  );
  isPtrs: boolean;
  subAccountId: string;

  templates: Drafts[];
  enableUseSloaContainer: boolean = false;
  templateCards: StandingAuthorizationCard[] = [];
  cardModel: CardControl = {
    title: 'Select a template',
    controlName: 'templateId',
    radios: [],
    validationMessage: 'Select a template',
  };

  constructor(private store: Store, private ffs: FeatureFlagService) {}

  ngOnInit(): void {
    createControl(this.parentForm, 'useTemplates', '', [Validators.required]);

    this.store.select(selectIsPtrs).pipe(takeUntil(this.destroy$)).subscribe(isPtrs => { this.isPtrs = isPtrs })
    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => { this.subAccountId = subAccountId })

    let useTemplate$ = this.store.select(selectUseTemplateVal).pipe(filter(useTemplate => !!useTemplate));

    this.store.select(selectEnableUseSloaContainer).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.enableUseSloaContainer = val;
    });

    combineLatest([useTemplate$, this.templates$])
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe(([useTemplate, templates]) => {
        this.templates = templates;
        if (useTemplate === 'yes' && templates.length === 0) {
          this.parentForm.get('useTemplates')?.setValue('no');
          this.showTemplateOptions = true;
          this.parentForm.removeControl('templateId');
          return;
        }

        if (useTemplate === 'yes') {
          createControl(this.parentForm, 'templateId', '', [
            Validators.required,
          ]);
          this.cardModel.radios = templates.map((template) => {
            let label = [];
            if (template.isIsoCompliant) {
              label = [
                { text: template.nickname ? template.nickname : '' },
                { label: 'ABA: ', text: template.beneficiaryBankId, isNewGrid: true },
                { label: 'Address: ', text: this.getConcatedAddressForISO(template?.beneficiary), isNewGrid: true },
                { label: 'Profile type: ', text: template.isThirdParty ? Constants.wireThirdParty : Constants.wireFirstParty, isNewGrid: true },
              ];
            } else {
              label = [
                { text: template.nickname ? template.nickname : '' },
                { label: 'ABA: ', text: template.beneficiaryBankId, isNewGrid: true },
                { label: 'Address: ', text: `${template.beneficiary?.addressLineOne}, ${template.beneficiary?.addressLineTwo}`, isNewGrid: true },
                { label: 'Profile type: ', text: template.isThirdParty ? Constants.wireThirdParty : Constants.wireFirstParty, isNewGrid: true },
              ];
            }
            return {
              value: template.id,
              label,
            };
          });

          if (this.cardModel.radios.length === 1) {
            this.templateId?.setValue(
              this.cardModel?.radios[0]?.value
            );
          }

          if (this.enableUseSloaContainer) {
            this.updateTemplateCards();
          }

          this.showTemplateOptions = true;
        } else {
          this.showTemplateOptions = false;
          this.templateCards = [];
          this.parentForm.removeControl('templateId');
        }
      });

    let templateId$ = this.store.select(selectTemplateId).pipe(
      withLatestFrom(this.store.select(selectIsFromEdit)),
      filter(([id, isFromEdit]) => !!id && !isFromEdit)
    );
    
    templateId$
    .pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectWireInputV3CallFinished)),
    )
    .subscribe(() => {
      this.callWireInputV3TemplateFlow();
    });


    this.store
      .select(selectWireInputV3CallFailed)
      .pipe(
        takeUntil(this.destroy$),
        filter((inputCallV3Failed) => inputCallV3Failed)
      )
      .subscribe(() => {
        this.templateId?.reset('');
      });
  }
  
  getConcatedAddressForISO(address: MailingDetails){
    return `${address?.buildingNumber ?? ''} ${address?.streetName ?? ''} ${address?.room ?? ''} ${address?.department ?? ''} ${address?.buildingName ?? ''}
    ${address?.floor ?? ''} ${address?.townLocationName ?? ''} ${address?.townName ?? ''} ${address?.districtName ?? ''} 
    ${address?.countrySubDivision ?? ''} ${address?.zipCode ?? ''}`
  }

  ngOnDestroy(): void {
    this.parentForm.removeControl('useTemplates');
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngAfterViewInit(): void {
    AdobeHelpers.trackClick('useTemplates-yes', AdobeConstants.ADOBE_+AdobeConstants._USESAVEDTEMPLATE_FORM);
  }

  get templateId(): UntypedFormControl {
    return this.parentForm?.get('templateId') as UntypedFormControl;
  }

  onTemplateCardSelected(cardId: string): void {
    this.templateId?.setValue(cardId);
    this.templateId?.markAsTouched();
    this.templateCards = this.templateCards.map(card => ({ ...card, selected: card.id === cardId }));
  }

  private updateTemplateCards(): void {
    this.templateCards = this.cardModel.radios.map((radio) => {
      const [firstLine, ...details] = radio.label.map((line) => {
        const prefix = line.label ? `${line.label}` : '';
        return `${prefix}${line.text}`.trim();
      });

      return {
        id: String(radio.value),
        title: firstLine || '',
        details,
        selected: this.templateId?.value === radio.value,
      };
    });
  }

  callWireInputV3TemplateFlow(): void {
    this.store.dispatch(WireActions.callWireInputV3TemplateFlow());    
    this.store.dispatch(ModifiedFirstPartiesActions.getModifiedFirstParties({ subaccount: this.subAccountId, transactionType: 'wires' }));
    if (this.isPtrs) {
      this.store.dispatch(PtrsActions.callPtrs({ accountId: this.subAccountId }))
    }
  }
}
